import { getGlobalSearchItemNavigationIcon } from "@frontend-shared/utils";
import type { LucideIcon } from "lucide-react";
import { Search } from "lucide-react";
import { memo, useMemo } from "react";

import { NavigationIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

import {
  GLOBAL_SEARCH_ITEM_ICONS,
  GLOBAL_SEARCH_NAVIGATION_ICON_STYLE,
  GLOBAL_SEARCH_ROW_ICON_CLASS_NAME,
} from "./constants";
import type { IGlobalSearchItemLogoProps } from "./types";
import {
  EGlobalSearchLogoKind,
  getGlobalSearchItemLogo,
} from "./utils/get-global-search-item-logo";

const ICON_CLASS_NAME = "h-[18px] w-[18px]";

// NOTE: NSAI-6853 — the leading picture of a result row, in order of how much
// it tells apart: the result's own logo (model provider, integration, file
// type, agent kind), then the sidebar entry its destination lives under
// (NSAI-6812), then the generic icon of its type.
const GlobalSearchItemLogoComponent = ({
  item,
}: IGlobalSearchItemLogoProps): JSX.Element => {
  const logo = useMemo(() => getGlobalSearchItemLogo(item), [item]);
  const navigationIcon = getGlobalSearchItemNavigationIcon(item);
  const TypeIcon: LucideIcon = GLOBAL_SEARCH_ITEM_ICONS[item.type] ?? Search;

  if (logo?.kind === EGlobalSearchLogoKind.BRAND) {
    return (
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-logo-tile-background p-1.5 shadow-[inset_0_0_0_1px_var(--glass-edge)]"
        data-testid="global-search-item-logo-brand"
      >
        <logo.Icon className="size-full" />
      </span>
    );
  }

  if (logo) {
    return (
      <span
        className={GLOBAL_SEARCH_ROW_ICON_CLASS_NAME}
        data-testid={`global-search-item-logo-${logo.kind}`}
      >
        <logo.Icon className={cn(ICON_CLASS_NAME, logo.colorClassName)} />
      </span>
    );
  }

  return (
    <span className={GLOBAL_SEARCH_ROW_ICON_CLASS_NAME}>
      {navigationIcon === undefined ? (
        <TypeIcon className={ICON_CLASS_NAME} />
      ) : (
        <NavigationIcon
          icon={navigationIcon}
          stroke="currentColor"
          style={GLOBAL_SEARCH_NAVIGATION_ICON_STYLE}
          className={ICON_CLASS_NAME}
        />
      )}
    </span>
  );
};

export const GlobalSearchItemLogo = memo(GlobalSearchItemLogoComponent);
